import { RouteObject } from 'react-router-dom';
import { AdministrationPageModule } from './Administration.Module';
import { routes } from './routes';
import { useRoleBasedAccess } from '@app/main/utils';
import { iconColor } from '@app/ui';
import { features } from '@app/base';
import { useMemo } from 'react';

export function useAdministrationModuleRoute() {
  const roleAccessMap = useRoleBasedAccess();

  return useMemo(() => {
    /**
     * No index route might be specified as it would be appended for a first available indexable route
     * Order might be same as on AdministrationPageModule.tsx
     */
    const CHILDREN: RouteObject[] = routes([
      roleAccessMap.OrganizationManagementRoles && {
        path: 'organization-settings',
        indexable: true,
        lazy: () =>
          import('./Organizations.Settings/OrganizationSettingsPage').then(
            ({ OrganizationSettingsPage }) => ({
              Component: OrganizationSettingsPage,
            }),
          ),
      },
      roleAccessMap.EmployeeManagementRoles && {
        path: 'employee',
        indexable: true,
        lazy: () =>
          import('./Employees.List/EmployeesListPage').then(({ EmployeesListPage }) => ({
            Component: EmployeesListPage,
          })),
      },
      roleAccessMap.EmployeeManagementRoles && {
        path: 'employee/invite',
        lazy: () =>
          import('./Employees.Add/EmployeeAddPage').then(({ EmployeeAddPage }) => ({
            Component: EmployeeAddPage,
          })),
      },
      roleAccessMap.EmployeeManagementRoles && {
        path: 'employee/:id',
        lazy: () =>
          import('./Employees.Details/EmployeeDetailsPage').then(({ EmployeeDetailsPage }) => ({
            Component: EmployeeDetailsPage,
          })),
      },
      roleAccessMap.ActivityAccessRoles &&
        features.isEnabled('ActivityPage') && {
          path: 'activity',
          indexable: true,
          lazy: () =>
            import('./Activities.List/ActivityListPage').then(({ ActivityListPage }) => ({
              Component: ActivityListPage,
            })),
        },
      roleAccessMap.ActivityAccessRoles &&
        features.isEnabled('ActivityPage') && {
          path: 'activity/:id',
          lazy: () =>
            import('./Activities.Details/ActivityDetailsPage').then(({ ActivityDetailsPage }) => ({
              Component: ActivityDetailsPage,
            })),
        },
      roleAccessMap.TemplateManagementRoles && {
        path: 'appointment-type-template',
        indexable: true,
        lazy: () =>
          import('./AppointmentTypeTemplates.List/AppointmentTypeTemplatesListPage').then(
            ({ AppointmentTypeTemplatesListPage }) => ({
              Component: AppointmentTypeTemplatesListPage,
            }),
          ),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'encounter-template',
        indexable: true,
        lazy: () =>
          import('./EncounterTemplates.List/EncounterTemplatesListPage').then(
            ({ EncounterTemplatesListPage }) => ({
              Component: EncounterTemplatesListPage,
            }),
          ),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'consent-template',
        indexable: true,
        lazy: () =>
          import('./ConsentTemplates.List/ConsentTemplatesListPage').then(({ ConsentTemplatesListPage }) => ({
            Component: ConsentTemplatesListPage,
          })),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'consent-template/add',
        lazy: () =>
          import('./ConsentTemplates.Add/AddConsentTemplatePage').then(({ AddConsentTemplatePage }) => ({
            Component: AddConsentTemplatePage,
          })),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'consent-template/:consentTemplateId',
        lazy: () =>
          import('./ConsentTemplates.Details/ConsentTemplateDetailsPage').then(
            ({ ConsentTemplateDetailsPage }) => ({
              Component: ConsentTemplateDetailsPage,
            }),
          ),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'dynamic-form-template/add',
        lazy: () =>
          import('./DynamicFormTemplates.Add/AddDynamicFormTemplatePage').then(
            ({ AddDynamicFormTemplatePage }) => ({
              Component: AddDynamicFormTemplatePage,
            }),
          ),
      },
      roleAccessMap.LookupManagementRoles && {
        path: 'specialty',
        indexable: true,
        lazy: () =>
          import('./Options.List/OptionListPage').then(({ OptionListPage }) => ({
            element: (
              <OptionListPage
                discriminator="specialty"
                key="specialty"
                icon="layout"
                color={iconColor('specialty')}
              />
            ),
          })),
      },
      roleAccessMap.LookupManagementRoles && {
        path: 'care-home',
        indexable: true,
        lazy: () =>
          import('./CatalogCareHomes.List/CatalogCareHomesListPage').then(({ CatalogCareHomesListPage }) => ({
            element: <CatalogCareHomesListPage />,
          })),
      },
      roleAccessMap.TemplateManagementRoles && {
        path: 'document-tag',
        indexable: true,
        lazy: () =>
          import('./DocumentTags.List/DocumentTagsListPage').then(({ DocumentTagsListPage }) => ({
            Component: DocumentTagsListPage,
          })),
      },
    ]);

    return {
      path: '/administration',
      element: <AdministrationPageModule />,
      children: CHILDREN,
    } satisfies RouteObject;
  }, [roleAccessMap]);
}
